'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useTranslation } from '@/hooks/useTranslation';
import { createClient } from '@/lib/supabase/client';
import { useTheme } from '@/hooks/useTheme';

export default function SignUpPage() {
    const { t } = useTranslation();
    const router = useRouter();
    const { theme, toggleTheme } = useTheme();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: 'visitor' as 'visitor' | 'manufacturer' | 'owner',
        agreedToTerms: false,
    });
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        // Validate passwords match
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            setLoading(false);
            return;
        }

        // Validate password strength
        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters long');
            setLoading(false);
            return;
        }

        try {
            const supabase = createClient();
            const { data, error } = await supabase.auth.signUp({
                email: formData.email,
                password: formData.password,
                options: {
                    emailRedirectTo: `${window.location.origin}/auth/callback`,
                    data: {
                        full_name: formData.name,
                        role: formData.role,
                    },
                },
            });

            if (error) {
                setError(error.message);
                return;
            }

            if (data.user) {
                setSuccess(true);
                // Redirect to email confirmation page
                setTimeout(() => {
                    router.push(`/auth/confirm-email?email=${encodeURIComponent(formData.email)}`);
                }, 1500);
            }
        } catch (err) {
            setError('An unexpected error occurred. Please try again.');
            console.error('Sign up error:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <main className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center px-6 py-12">
            {/* Theme Toggle Button */}
            <button
                onClick={toggleTheme}
                className="fixed top-6 right-6 p-3 bg-card border border-border rounded-lg shadow-lg hover:bg-accent/10 transition-colors duration-200 z-50"
                aria-label="Toggle theme"
            >
                {theme === 'light' ? (
                    <Icon name="MoonIcon" size={20} variant="outline" className="text-foreground" />
                ) : (
                    <Icon name="SunIcon" size={20} variant="outline" className="text-foreground" />
                )}
            </button>

            <div className="w-full max-w-md">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="inline-block px-6 py-2 bg-accent/20 border border-accent text-accent rounded-full text-sm font-semibold mb-4">
                        {t('heroTagline')}
                    </div>
                    <h1 className="text-3xl font-headline font-bold text-foreground mb-2">
                        Create Your Account
                    </h1>
                    <p className="text-muted-foreground">
                        Join EgypTex Gateway to connect with verified manufacturers
                    </p>
                </div>

                {/* Sign Up Card */}
                <div className="bg-card border border-border rounded-2xl shadow-lg p-8">
                    {/* Success Message */}
                    {success && (
                        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-sm text-green-600">
                                Account created successfully! Redirecting to confirmation page...
                            </p>
                        </div>
                    )}

                    {/* Error Message */}
                    {error && (
                        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-red-600">{error}</p>
                        </div>
                    )}

                    {/* Sign Up Form */}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Role Selection */}
                        <div>
                            <label className="block text-sm font-semibold text-foreground mb-2">
                                I am a:
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, role: 'visitor' })}
                                    className={`px-4 py-3 rounded-lg border-2 font-semibold text-sm transition-all ${formData.role === 'visitor'
                                        ? 'border-primary bg-primary/10 text-primary'
                                        : 'border-border text-muted-foreground hover:border-primary/50'
                                        }`}
                                >
                                    <Icon name="UserIcon" size={20} variant="outline" className="mx-auto mb-1" />
                                    Visitor
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, role: 'manufacturer' })}
                                    className={`px-4 py-3 rounded-lg border-2 font-semibold text-sm transition-all ${formData.role === 'manufacturer'
                                        ? 'border-primary bg-primary/10 text-primary'
                                        : 'border-border text-muted-foreground hover:border-primary/50'
                                        }`}
                                >
                                    <Icon name="BuildingOfficeIcon" size={20} variant="outline" className="mx-auto mb-1" />
                                    Manufacturer
                                </button>
                            </div>
                        </div>

                        {/* Name */}
                        <div>
                            <label htmlFor="name" className="block text-sm font-semibold text-foreground mb-2">
                                Full Name
                            </label>
                            <input
                                id="name"
                                type="text"
                                required
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                placeholder="Enter your full name"
                            />
                        </div>

                        {/* Email */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-2">
                                Email Address
                            </label>
                            <input
                                id="email"
                                type="email"
                                required
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                placeholder="you@company.com"
                            />
                        </div>

                        {/* Password */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-semibold text-foreground mb-2">
                                Password
                            </label>
                            <input
                                id="password"
                                type="password"
                                required
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                placeholder="Create a strong password"
                            />
                        </div>

                        {/* Confirm Password */}
                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-semibold text-foreground mb-2">
                                Confirm Password
                            </label>
                            <input
                                id="confirmPassword"
                                type="password"
                                required
                                value={formData.confirmPassword}
                                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                placeholder="Re-enter your password"
                            />
                        </div>

                        {/* Terms Agreement */}
                        <div className="flex items-start gap-3">
                            <input
                                id="terms"
                                type="checkbox"
                                checked={formData.agreedToTerms}
                                onChange={(e) => setFormData({ ...formData, agreedToTerms: e.target.checked })}
                                className="mt-1 w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                            />
                            <label htmlFor="terms" className="text-sm text-muted-foreground">
                                I agree to the{' '}
                                <a href="/terms-of-service" className="text-primary hover:underline">
                                    Terms of Service
                                </a>{' '}
                                and{' '}
                                <a href="/privacy-policy" className="text-primary hover:underline">
                                    Privacy Policy
                                </a>
                            </label>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={!formData.agreedToTerms || loading}
                            className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Creating Account...' : 'Create Account'}
                        </button>
                    </form>

                    {/* Divider */}
                    <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-border"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-4 bg-card text-muted-foreground">Or continue with</span>
                        </div>
                    </div>

                    {/* Google Sign Up Button */}
                    <button
                        type="button"
                        onClick={async () => {
                            try {
                                // Store role in localStorage to retrieve after OAuth callback
                                localStorage.setItem('pendingUserRole', formData.role);

                                const supabase = createClient();
                                const { error } = await supabase.auth.signInWithOAuth({
                                    provider: 'google',
                                    options: {
                                        redirectTo: `${window.location.origin}/auth/callback`,
                                    },
                                });
                                if (error) {
                                    setError(error.message);
                                }
                            } catch (err) {
                                console.error('Google sign up error:', err);
                                setError('Failed to sign up with Google');
                            }
                        }}
                        className="w-full px-6 py-3 bg-card border-2 border-border text-foreground rounded-lg font-semibold hover:bg-accent/10 transition-colors duration-200 flex items-center justify-center gap-3"
                    >
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        Sign up with Google
                    </button>


                    {/* Sign In Link */}
                    <p className="text-center text-sm text-muted-foreground mt-6">
                        Already have an account?{' '}
                        <a href="/signin" className="text-primary font-semibold hover:underline">
                            Sign In
                        </a>
                    </p>
                </div>

                {/* Back to Home */}
                <div className="text-center mt-6">
                    <a href="/homepage" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-2">
                        <Icon name="ArrowLeftIcon" size={16} variant="outline" />
                        Back to Homepage
                    </a>
                </div>
            </div>
        </main>
    );
}
